<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h5 class="modal-title">Initiate Lab Test</h5>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>

      <div class="modal-body">
        <loader v-if="loading" />
      </div>

      <div v-if="!loading" class="modal-body">
        <!-- Specimen Information Section -->
        <h6 class="section-title">Specimen Information</h6>
        <div class="specimen-info">
          <!-- Specimen ID -->
          <div class="form-group">
            <label>Specimen ID</label>
            <input
              v-model="form.specimen_id"
              type="text"
              class="form-control"
              placeholder="e.g. S456789"
            />
          </div>

          <!-- Specimen Category -->
          <div class="form-group">
            <label>Specimen Category</label>
            <select
              v-model="form.category"
              class="form-control"
              @change="updateTypes"
            >
              <option value="">-- Select Category --</option>
              <option
                v-for="(types, category) in specimenTypes"
                :key="category"
              >
                {{ category }}
              </option>
            </select>
          </div>

          <!-- Specimen Type (filtered by category) -->
          <div class="form-group">
            <label>Specimen Type</label>
            <select v-model="form.type" class="form-control">
              <option value="">-- Select Type --</option>
              <option v-for="type in availableTypes" :key="type">
                {{ type }}
              </option>
            </select>
          </div>

          <!-- Collection Date -->
          <div class="form-group">
            <label>Collection Date & Time</label>
            <input
              v-model="form.collection_date"
              type="datetime-local"
              class="form-control"
            />
          </div>

          <!-- Condition -->
          <div class="form-group" style="position: relative">
            <label for="condition">Condition</label>

            <div class="custom-select2" @click="toggleDropdown" ref="dropdown">
              <span>{{ selectedCondition || "Select condition..." }}</span>
              <i class="arrow" :class="{ open: showDropdown }"></i>
            </div>

            <!-- Dropdown -->
            <div v-if="showDropdown" class="custom-select2-dropdown">
              <input
                type="text"
                v-model="searchTerm"
                class="select2-search"
                placeholder="Search..."
                @click.stop
              />
              <ul class="select2-options">
                <li
                  v-for="(option, i) in filteredOptions"
                  :key="i"
                  @click.stop="selectOption(option)"
                >
                  {{ option }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Collector -->
          <div class="form-group">
            <label>Collector</label>
            <input
              v-model="form.collector"
              type="text"
              class="form-control"
              placeholder="e.g. Nurse Jane"
            />
          </div>

          <!-- Storage -->
          <div class="form-group">
            <label>Storage Location</label>
            <input
              v-model="form.storage"
              type="text"
              class="form-control"
              placeholder="e.g. Refrigerator - Rack B2"
            />
          </div>
        </div>

        <!-- Expected Test Details -->
        <h6 class="section-title">Expected Test Details</h6>
        <div class="expected-details">
          <div class="form-group">
            <label>Expected Duration (hours)</label>
            <input
              v-model="form.expected_duration"
              type="text"
              class="form-control"
              placeholder="e.g. 2.5"
              @input="validateDurationInput"
              @change="autoCalculateExpectedCompletion"
            />
          </div>

          <div class="form-group">
            <label>Expected Completion Time</label>
            <input
              v-model="form.expected_completion"
              type="datetime-local"
              class="form-control"
              @change="autoCalculateExpectedDuration"
            />
          </div>

          <div class="form-group">
            <label>Remarks</label>
            <textarea
              v-model="form.remarks"
              class="form-control"
              rows="2"
              placeholder="Any additional notes..."
            ></textarea>
          </div>
        </div>
      </div>

      <div v-if="!loading" class="modal-footer">
        <button type="button" class="btn btn-secondary" @click="closeModal">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="submit_data">
          Initiate
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import specimen_types from "./specimen_types";
import specimen_conditions from "./specimen_conditions";
import { generalApiRequest } from "../../../api/apiIndex";
import loader from "../../special_clinics/dialysis/loader.vue";
// import showNotification from "../../../notification/notify";
export default {
  name: "InitiateLabTestModal",
  props: {
    test_id: {
      type: [String, Number],
      required: true,
    },
    update_mode: {
      type: Boolean,
      required: true,
    },
    update_data: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      selectedCondition: "",
      showDropdown: false,
      searchTerm: "",
      conditionOptions: specimen_conditions,
      specimenTypes: specimen_types,

      availableTypes: [],
      loading: false,
      form: {
        specimen_id: "",
        category: "",
        type: "",
        collection_date: "", //new Date().toISOString().slice(0, 16),
        condition: "",
        collector: "",
        storage: "",
        expected_duration: "",
        expected_completion: "",
        remarks: "",
        selectedCondition: "",
      },
    };
  },
  components: {
    loader,
  },
  computed: {
    filteredOptions() {
      const s = this.searchTerm.toLowerCase();
      return this.conditionOptions.filter((o) => o.toLowerCase().includes(s));
    },
  },
  methods: {
    updateTypes() {
      this.availableTypes = this.specimenTypes[this.form.category] || [];
      this.form.type = "";
    },

    // Validate duration input to accept only numbers with up to 2 decimal places
    validateDurationInput(event) {
      let value = event.target.value;

      // Remove any non-numeric characters except decimal point
      value = value.replace(/[^0-9.]/g, "");

      // Ensure only one decimal point
      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts.slice(1).join("");
      }

      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + "." + parts[1].substring(0, 2);
      }

      this.form.expected_duration = value;
    },

    // Auto-calculate expected completion time based on duration
    autoCalculateExpectedCompletion() {
      if (!this.form.expected_duration) return;

      const duration = parseFloat(this.form.expected_duration);
      if (isNaN(duration) || duration <= 0) return;

      // Use current time as the base
      const now = new Date();

      // Add the duration hours
      now.setTime(now.getTime() + duration * 60 * 60 * 1000);

      // Format to datetime-local format
      this.form.expected_completion = now.toISOString().slice(0, 16);
    },

    // Auto-calculate expected duration based on completion time
    autoCalculateExpectedDuration() {
      if (!this.form.expected_completion) return;

      const completionTime = new Date(this.form.expected_completion);
      const now = new Date();

      // Calculate difference in hours
      const diffMs = completionTime - now;
      const diffHours = diffMs / (1000 * 60 * 60);

      if (diffHours > 0) {
        // Format to 2 decimal places
        this.form.expected_duration = diffHours.toFixed(2);
      }
    },

    closeModal() {
      this.$emit("close-initiate-lab-modal");
    },

    //============================================================
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      this.$nextTick(() => {
        if (this.showDropdown) this.$refs.dropdown?.focus();
      });
    },
    selectOption(option) {
      this.form = { ...this.form, selectedCondition: option };
      this.selectedCondition = option;
      this.showDropdown = false;
      this.searchTerm = "";
    },
    async submit_data() {
      this.loading = true;
      // const url = `lab_specimens/store/`;

      this.form = { ...this.form, test_id: this.test_id };
      await generalApiRequest("lab_specimens/store", "POST", this.form, 1);
      // try {
      //   this.form = { ...this.form, test_id: this.test_id };
      //   await generalApiRequest(url, "POST", this.form, 1);
      //   // const response =
      //   // console.log(response);
      //   // this.consumables = response.consumables;
      // } catch (error) {
      //   console.error("Error fetching services:", error);
      //   alert("Error fetching services:", error);
      // }
      this.loading = false;
      this.closeModal();
    },
    handleOutsideClick(e) {
      const dropdown = this.$refs.dropdown?.parentNode;
      if (dropdown && !dropdown.contains(e.target)) {
        this.showDropdown = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    this.form = { ...this.form, type: this.update_data.type };
    if (this.update_mode) {
      this.form = this.update_data;
    }
    this.updateTypes();
    setTimeout(() => {
      console.log(this.update_data);

      this.form = { ...this.form, type: this.update_data.type };
    }, 1000);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
};
</script>

<style scoped>
.modal-body {
  padding: 1rem;
  overflow-y: auto;
}

/* Section titles */
.section-title {
  font-weight: bold;
  margin-top: 1.2rem;
  margin-bottom: 0.6rem;
  color: #333;
  border-bottom: 1px solid #ddd;
  padding-bottom: 4px;
}

/* Apply grid layout to sections */
.specimen-info,
.expected-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem 1.2rem;
  margin-bottom: 1.5rem;
}

/* Form groups styling */
.form-group {
  display: flex;
  flex-direction: column;
}

/* Inputs */
.form-control {
  width: 100%;
  padding: 0.45rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
}

/* Footer styling */
.modal-footer {
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  padding-top: 0.8rem;
}

/* Close button */
.close {
  background: rgb(233, 22, 22);
  border: 1px solid rgb(182, 178, 178);
  font-size: 1.5rem;
  color: white;
  line-height: 1;
  cursor: pointer;
}

/* Custom dropdown (Select2-style) */
.custom-select2 {
  border: 1px solid #ccc;
  padding: 8px 10px;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.custom-select2 .arrow {
  border: solid #777;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 4px;
  transform: rotate(45deg);
  transition: 0.2s;
}
.custom-select2 .arrow.open {
  transform: rotate(-135deg);
}

.custom-select2-dropdown {
  position: absolute;
  z-index: 10;
  width: 100%;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.select2-search {
  width: 100%;
  padding: 6px;
  border: none;
  border-bottom: 1px solid #ddd;
  outline: none;
}

.select2-options {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 150px;
  overflow-y: auto;
}

.select2-options li {
  padding: 6px 10px;
  cursor: pointer;
}

.select2-options li:hover {
  background-color: #f1f1f1;
}
</style>
